// src/utils/toolStorage.js
//
// Per-account local storage for the eight toolkit tools.
//
// The tools used to write to flat keys - `friendshipGriefData`,
// `boundaryBlueprintData` and so on - with no account scoping. On a shared
// device (a family iPad, a school laptop, a sibling's browser) the next
// person to open the app saw the previous person's data, which for the Grief
// Companion means their unsent letters: the thing the tool itself describes
// as "everything you never got to say" and "just for you".
//
// Keys are now scoped per uid, signing out clears the scope, and anything
// written under the old flat key is migrated into the current scope once so
// nobody loses work they have already done.

const PREFIX = 'fv:tool';
const ANON = 'anon';

// The flat keys the tools used before scoping existed.
export const LEGACY_TOOL_KEYS = [
  'socialBatteryData',
  'groupDynamicsData',
  'conflictSimulatorData',
  'digitalDramaShieldData',
  'friendshipGriefData',
  'peopleFinderData',
  'boundaryBlueprintData',
  'socialPainLabData',
];

// Tools have no auth context of their own, so the scope is resolved here.
// Signed-out use is kept under `anon` rather than being refused - the tools
// deliberately work without an account.
let authRef = null;
export function registerToolStorageAuth(auth) {
  authRef = auth;
}

const currentUid = () => {
  try {
    return authRef?.currentUser?.uid || null;
  } catch {
    return null;
  }
};

const scopeFor = (uid) => (uid !== undefined && uid !== null ? String(uid) : (currentUid() || ANON));
const scopedKey = (toolKey, uid) => `${PREFIX}:${scopeFor(uid)}:${toolKey}`;

/**
 * Read a tool's saved data for this account.
 *
 * On first read for a signed-in user, anything left under the old flat key is
 * moved into their scope and the flat key removed - so existing work follows
 * the person who was using the device, and stops being visible to the next one.
 */
export function loadToolData(toolKey, uid) {
  try {
    const key = scopedKey(toolKey, uid);
    const scoped = localStorage.getItem(key);
    if (scoped !== null) return JSON.parse(scoped);

    const legacy = localStorage.getItem(toolKey);
    if (legacy !== null) {
      localStorage.setItem(key, legacy);
      localStorage.removeItem(toolKey);
      return JSON.parse(legacy);
    }
    return null;
  } catch {
    // Private windows, cleared site data, or storage disabled entirely.
    return null;
  }
}

// Two things legitimately want to know that a save happened:
//
//   - the hub sync (ToolSyncService), which marks the tool dirty and pushes
//   - the "Saved" indicator the tools show, so a person can see that pressing
//     Save actually did something
//
// The sync keeps its single slot; the UI gets a list, because any number of
// mounted tools may be listening. Neither import this module's callers, so
// there is no circular import and everything still works if sync is never
// wired up at all.
let onSaved = null;
export function registerToolSyncHook(fn) {
  onSaved = typeof fn === 'function' ? fn : null;
}

const saveListeners = new Set();

/**
 * Be told whenever a tool saves. Returns an unsubscribe function.
 *
 * Listeners fire only after the write has succeeded, so an indicator driven
 * by this cannot claim a save that did not happen.
 */
export function subscribeToSaves(fn) {
  if (typeof fn !== 'function') return () => {};
  saveListeners.add(fn);
  return () => saveListeners.delete(fn);
}

/**
 * Save a tool's data for this account. Never throws.
 *
 * Stamps __updatedAt so hub sync can tell which copy is newer without
 * merging two personal journals field by field.
 */
export function saveToolData(toolKey, data, uid, opts = {}) {
  try {
    // `fromSync` writes a copy pulled DOWN from the hub. It must keep the
    // remote timestamp and must not mark the tool dirty - otherwise hydrating
    // re-stamps everything to now and immediately pushes it back, which both
    // destroys the newer/older comparison and loops.
    const fromSync = opts.fromSync === true;
    const stamped =
      !fromSync && data && typeof data === 'object' && !Array.isArray(data)
        ? { ...data, __updatedAt: Date.now() }
        : data;
    localStorage.setItem(scopedKey(toolKey, uid), JSON.stringify(stamped));
    // Local write already succeeded; sync is best-effort and must never
    // affect whether the user's work was saved.
    if (!fromSync) {
      try { onSaved?.(toolKey); } catch { /* sync is optional */ }
      // Deliberately not fired for a sync write: a copy arriving from the hub
      // is not something the person just did, and flashing "Saved" at them for
      // it would be a lie.
      saveListeners.forEach((fn) => {
        try { fn(toolKey); } catch { /* indicators are cosmetic */ }
      });
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Remove every tool's data for one account. Called on sign-out so private
 * writing does not sit on a shared device waiting for the next person.
 */
export function clearToolData(uid) {
  let removed = 0;
  try {
    for (const toolKey of LEGACY_TOOL_KEYS) {
      const key = scopedKey(toolKey, uid);
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
        removed += 1;
      }
      // Sweep any stragglers still on the unscoped key.
      if (localStorage.getItem(toolKey) !== null) {
        localStorage.removeItem(toolKey);
        removed += 1;
      }
    }
  } catch {
    // Nothing to do - storage unavailable.
  }
  return removed;
}

/** True when this device is holding tool data for the given account. */
export function hasToolData(uid) {
  try {
    return LEGACY_TOOL_KEYS.some(
      (toolKey) => localStorage.getItem(scopedKey(toolKey, uid)) !== null,
    );
  } catch {
    return false;
  }
}
